import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation } from "react-router-dom";
import { X, Send, Loader2, BookOpen } from "lucide-react";
import api from "@/lib/api";

/**
 * Floating AI assistant available across the app. Grounded in the "Manual do
 * Cuidador" (RAG) + current lead context when the user is on a lead route.
 *
 * variant:
 *   - "floating" (default): trigger is a pill FAB fixed at bottom-right.
 *   - "sidebar":  trigger is a compact full-width button, meant to sit inside
 *                 the Shell sidebar footer next to the clock-in widget.
 *   In both variants the panel opens as a fixed drawer on the right.
 */
export default function ManualAssistant({ variant = "floating" } = {}) {
  const { id } = useParams();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', text, sources }
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Only show the assistant when we're inside a lead context (has id)
  const leadId = id || (location.pathname.match(/\/leads\/([\w-]+)/) || [])[1];
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  if (!leadId) return null;

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    setMessages(m => [...m, { role: "user", text: question }]);
    setQ("");
    setLoading(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/manual-chat`, { question });
      setMessages(m => [...m, { role: "assistant", text: data.answer, sources: data.sources || [] }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", text: e.response?.data?.detail || "Não consegui responder agora. Tenta novamente.", sources: [] }]);
    } finally { setLoading(false); }
  };

  const trigger = variant === "sidebar" ? (
    <button
      onClick={() => setOpen(true)}
      data-testid="manual-fab"
      className="w-full inline-flex items-center gap-2 text-[12px] text-muted-foreground hover:text-foreground hover:bg-card transition-colors px-2 py-1.5 rounded-md mb-2 font-heading"
      title="Assistente Caring — Manual do Cuidador"
    >
      <BookOpen className="w-3.5 h-3.5" />
      <span className="truncate">Assistente Caring</span>
    </button>
  ) : (
    <button
      onClick={() => setOpen(true)}
      data-testid="manual-fab"
      className="fixed bottom-6 right-6 z-40 rounded-full bg-foreground hover:bg-slate-800 text-background shadow-lg px-5 py-3 flex items-center gap-2 text-sm font-semibold"
      title="Assistente Caring — Manual do Cuidador"
    >
      <BookOpen className="w-4 h-4" /> Assistente Caring
    </button>
  );

  return (
    <>
      {!open && trigger}
      {open && createPortal(
        <div className="fixed bottom-6 right-6 z-40 w-[420px] max-h-[80vh] bg-card rounded-2xl shadow-2xl border border-border flex flex-col" data-testid="manual-assistant">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/40 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-foreground" />
              <div>
                <div className="font-ui text-sm text-foreground">Assistente Caring</div>
                <div className="text-[10px] text-muted-foreground">Grounded no Manual do Cuidador</div>
              </div>
            </div>
            <button onClick={()=>setOpen(false)} className="text-muted-foreground/70 hover:text-foreground" data-testid="manual-close"><X className="w-5 h-5" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="manual-messages">
            {messages.length === 0 && (
              <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
                <div>👋 Olá! Pergunta-me qualquer coisa sobre esta lead — dou-te respostas baseadas no Manual do Cuidador.</div>
                <div className="text-muted-foreground">Exemplos:</div>
                <ul className="list-disc pl-4 text-muted-foreground space-y-1">
                  <li>Que sinais de alerta devo procurar em Alzheimer avançado?</li>
                  <li>Como abordar a família sobre sobrecarga do cuidador?</li>
                  <li>Que perguntas fazer sobre continência e higiene?</li>
                </ul>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
                <div className={`inline-block max-w-[85%] rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-primary/25 text-foreground" : "bg-muted/40 text-foreground"}`} style={{whiteSpace: "pre-wrap"}}>
                  {m.text}
                </div>
                {m.sources?.length > 0 && (
                  <div className="mt-1 text-[10px] text-muted-foreground">Manual: {m.sources.slice(0, 3).join(" · ")}</div>
                )}
              </div>
            ))}
            {loading && (
              <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> A consultar o manual...</div>
            )}
          </div>

          <div className="p-3 border-t border-border flex gap-2">
            <input
              value={q}
              onChange={e=>setQ(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter" && !loading) ask(); }}
              placeholder="Escreve a tua pergunta..."
              className="flex-1 bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
              data-testid="manual-input"
            />
            <button onClick={ask} disabled={loading || !q.trim()}
              className="rounded-lg bg-foreground hover:bg-slate-800 text-background px-4 disabled:opacity-40"
              data-testid="manual-send">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
